import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Mail, Inbox, Send, RefreshCw, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";

const TEMPLATE_LABELS = {
  subscriber_welcome: "Subscriber welcome",
  subscriber_admin_welcome: "Admin-added subscriber welcome",
  balance_paid: "Balance paid",
  subscription_cancelled_admin: "Cancellation alert (admin)",
  waitlist_confirmation: "Waitlist confirmation",
  test_send: "Test send",
};

export default function ConsoleEmailOutbox() {
  const [emails, setEmails] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    const [outbox, stat] = await Promise.all([
      api.get(`/admin/email-outbox${statusFilter ? `?status=${statusFilter}` : ""}`),
      api.get(`/admin/emails/status`).catch(() => ({ data: null })),
    ]);
    setEmails(outbox.data.emails || []);
    setCounts(outbox.data.counts || {});
    setStatus(stat.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const sendTest = async () => {
    if (!testEmail.trim()) return setErr("Enter an email address first.");
    setBusy("test"); setErr(""); setToast("");
    try {
      const { data } = await api.post("/admin/emails/test", { to: testEmail.trim() });
      setToast(`Test sent to ${data.sent_to} - provider id ${data.provider_id}`);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const processQueue = async () => {
    setBusy("process"); setErr(""); setToast("");
    try {
      const { data } = await api.post("/admin/emails/process", { limit: 100 });
      setToast(`Processed ${data.processed} - sent ${data.sent}, failed ${data.failed}.`);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const retryOne = async (id) => {
    setBusy(`retry-${id}`); setErr("");
    try {
      const { data } = await api.post(`/admin/emails/${id}/retry`);
      setToast(data.ok ? `Resent ${id.slice(0, 8)}…` : `Retry failed: ${data.error}`);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="console-email-outbox">
      <div className="mb-8">
        <div className="bloom-divider mb-4 text-left">✦ Email Outbox ✦</div>
        <h1 className="font-serif text-5xl">Outgoing notifications</h1>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] mt-2">
          Every transactional email the platform sends, tracked end-to-end.
        </p>
      </div>

      {/* Provider status banner */}
      {status && (
        <div
          className={`bloom-card p-5 mb-6 border-l-4 flex items-start gap-3 ${status.configured ? "border-[var(--bloom-brand)]" : "border-[var(--bloom-warning)]"}`}
          data-testid="email-provider-status"
        >
          {status.configured
            ? <CheckCircle2 className="text-[var(--bloom-brand)] flex-shrink-0 mt-0.5" size={18} />
            : <AlertCircle className="text-[var(--bloom-warning)] flex-shrink-0 mt-0.5" size={18} />}
          <div className="flex-1">
            <div className="font-serif text-base">
              {status.configured ? "Resend connected" : "No email provider connected"}
            </div>
            <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-1">
              {status.configured
                ? <>Sending as <strong>{status.sender}</strong>. Replies route to <strong>{status.reply_to}</strong>. {status.queued + status.failed > 0 ? `${status.queued + status.failed} message(s) waiting to send.` : "All caught up."}</>
                : "Drop a RESEND_API_KEY into the backend env and queued emails will flush automatically."}
            </div>
          </div>
        </div>
      )}

      {/* Test send + process queue */}
      <div className="bloom-card p-5 mb-6">
        <div className="bloom-label mb-3">Send a test email</div>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="email"
            placeholder="you@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="bloom-input flex-1 min-w-[220px]"
            data-testid="email-test-input"
          />
          <button onClick={sendTest} disabled={busy === "test" || !status?.configured} className="bloom-btn bloom-btn-primary" data-testid="email-test-btn">
            <Send size={13} /> {busy === "test" ? "Sending…" : "Send test"}
          </button>
          <button onClick={processQueue} disabled={busy === "process" || !status?.configured || (status?.queued + status?.failed) === 0} className="bloom-btn bloom-btn-outline" data-testid="email-process-btn">
            <RefreshCw size={13} /> {busy === "process" ? "Processing…" : `Flush queue (${(status?.queued || 0) + (status?.failed || 0)})`}
          </button>
        </div>
        {toast && (
          <div className="mt-3 flex items-center gap-2 text-sm text-[var(--bloom-brand)] font-serif italic" data-testid="email-toast">
            <Sparkles size={13} /> {toast}
          </div>
        )}
        {err && (
          <div className="mt-3 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="email-error">
            <AlertCircle size={13} /> {err}
          </div>
        )}
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        {[
          ["", "All", (counts.queued || 0) + (counts.sent || 0) + (counts.failed || 0)],
          ["queued", "Queued", counts.queued || 0],
          ["sent", "Sent", counts.sent || 0],
          ["failed", "Failed", counts.failed || 0],
        ].map(([v, label, n]) => (
          <button
            key={label}
            onClick={() => setStatusFilter(v)}
            className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase transition border ${
              statusFilter === v
                ? "bg-[var(--bloom-ink)] text-[#FAF4FA] border-[var(--bloom-ink)]"
                : "bg-transparent text-[var(--bloom-ink-soft)] border-[var(--bloom-border)] hover:text-[var(--bloom-ink)]"
            }`}
            style={{ borderRadius: 999 }}
            data-testid={`outbox-filter-${v || "all"}`}
          >
            {label} ({n})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="font-serif italic text-[var(--bloom-ink-muted)] py-20 text-center">Loading…</div>
      ) : emails.length === 0 ? (
        <div className="bloom-card p-12 text-center">
          <Inbox className="mx-auto text-[var(--bloom-ink-muted)] mb-3" size={32} strokeWidth={1.2} />
          <div className="font-serif text-xl">No emails yet.</div>
          <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-1">Queued messages will appear here.</div>
        </div>
      ) : (
        <div className="bloom-card overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-[var(--bloom-border)]">
              <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[var(--bloom-ink-muted)]">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">To</th>
                <th className="px-4 py-3">Template</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {emails.map((e) => (
                <tr key={e.id} className="border-b border-[var(--bloom-border)]/40" data-testid={`outbox-${e.id}`}>
                  <td className="px-4 py-3 font-serif text-sm">{new Date(e.created_at).toLocaleString("en-NZ")}</td>
                  <td className="px-4 py-3 font-serif text-sm">{e.to_email}</td>
                  <td className="px-4 py-3 font-serif text-sm">
                    {TEMPLATE_LABELS[e.template] || e.template}
                    {e.error && <div className="text-[10px] text-[var(--bloom-accent)] mt-0.5 italic">{e.error}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase ${
                      e.status === "sent" ? "bg-[var(--bloom-brand)] text-white" :
                      e.status === "failed" ? "bg-[var(--bloom-accent)]/15 text-[var(--bloom-accent)]" :
                      "bg-[var(--bloom-surface)] text-[var(--bloom-ink-soft)]"
                    }`} style={{ borderRadius: 999 }}>
                      {e.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {(e.status === "queued" || e.status === "failed") && status?.configured && (
                      <button
                        onClick={() => retryOne(e.id)}
                        disabled={busy === `retry-${e.id}`}
                        className="text-xs font-serif italic text-[var(--bloom-brand)] hover:underline"
                        data-testid={`outbox-retry-${e.id}`}
                      >
                        {busy === `retry-${e.id}` ? "…" : "Resend"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
